<template>
  <div class="calculator-page">
    <header class="page-heading">
      <div>
        <p class="eyebrow">权益工具</p>
        <h1>积分发放计算器</h1>
        <p class="subtitle">按会员等级、叠加角色和商品贡献值试算，仅展示实际命中的权益。</p>
      </div>
      <el-button icon="el-icon-refresh-left" @click="resetForm">恢复示例数据</el-button>
    </header>

    <section class="scenario-card">
      <el-form label-position="top" :model="form" @submit.native.prevent>
        <div class="form-grid">
          <el-form-item label="邀请人 A 等级">
            <el-select v-model="form.inviterLevel">
              <el-option label="普通会员" value="ordinary" />
              <el-option label="星级会员" value="star" />
            </el-select>
          </el-form-item>
          <el-form-item label="邀请人 A 叠加角色">
            <el-select v-model="form.inviterRoles" multiple collapse-tags placeholder="无叠加角色">
              <el-option v-for="role in roleOptions" :key="role.value" :label="role.label" :value="role.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="被邀请人 B 等级">
            <el-select v-model="form.inviteeLevel">
              <el-option label="普通会员" value="ordinary" />
              <el-option label="星级会员" value="star" />
            </el-select>
          </el-form-item>
          <el-form-item label="被邀请人 B 角色（仅记录）">
            <el-select v-model="form.inviteeRoles" multiple collapse-tags placeholder="无叠加角色">
              <el-option v-for="role in roleOptions" :key="role.value" :label="role.label" :value="role.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="商品贡献值">
            <el-input-number v-model="form.contribution" :min="0.01" :precision="2" :controls="false" />
          </el-form-item>
          <el-form-item v-if="hasRole('partner')" label="合伙人个人直推销售占比">
            <el-input-number v-model="form.directSharePct" :min="0" :max="100" :precision="2" :controls="false" />
            <span class="input-suffix">%</span>
          </el-form-item>
        </div>

        <div v-if="hasRole('city') || isStarToStar" class="parameter-row">
          <span class="parameter-label"><i class="el-icon-setting" /> 角色参数</span>
          <template v-if="hasRole('city')">
            <el-switch v-model="form.cityProvinceActive" active-text="已开通省公司账户" />
            <label v-if="form.cityProvinceActive" class="inline-number">
              <span>市级销售占比</span>
              <el-input-number v-model="form.citySharePct" :min="0" :max="100" :precision="2" :controls="false" />
              <b>%</b>
            </label>
          </template>
          <el-switch
            v-if="isStarToStar"
            v-model="form.upgradedFromThisRelation"
            active-text="B 由本邀请关系从普通会员升级"
          />
        </div>

        <div class="form-actions">
          <el-button type="primary" icon="el-icon-s-data" @click="calculate">开始计算</el-button>
        </div>
      </el-form>
    </section>

    <el-alert
      v-if="errors.length"
      class="error-alert"
      title="暂时无法计算"
      :description="errors.join('；')"
      type="warning"
      show-icon
      closable
      @close="errors = []"
    />

    <div v-if="result" class="result-layout">
      <main class="result-card">
        <div class="section-heading">
          <div>
            <h2>规则明细台账</h2>
            <p>{{ inviterIdentity }} A 推荐 {{ inviteeIdentity }} B，B 本次产生 {{ formatNumber(form.contribution) }} 贡献值。</p>
          </div>
          <el-tag type="success" effect="plain"><i class="el-icon-circle-check" /> 计算完成</el-tag>
        </div>

        <section v-for="section in sections" :key="section.key" class="ledger-group" :class="`scope-${section.scope}`">
          <div class="ledger-group-title">
            <h3>{{ section.title }}</h3>
            <span v-if="section.scope === 'common'" class="scope-chip common">角色通用权益</span>
            <span v-if="section.scope === 'private'" class="scope-chip private">私域角色权益</span>
            <span v-if="section.scope === 'public'" class="scope-chip public">公域角色权益</span>
          </div>
          <div v-for="item in section.items" :key="item.id" class="formula-line">
            <div>
              <strong>{{ item.name }}</strong>
              <small>{{ item.formula }}</small>
              <mark v-if="item.eligibilityNote">{{ item.eligibilityNote }}</mark>
            </div>
            <p><span>=</span> <b>{{ formatNumber(item.amount) }}</b> {{ typeLabels[item.type] }}</p>
          </div>
        </section>

        <section class="totals-card">
          <div class="totals-title">
            <span>计算结论</span>
            <h3>A、B 分别获得</h3>
          </div>
          <template v-if="ledger.totals.mode === 'byScope'">
            <div class="invitee-total">
              <span>B · {{ inviteeIdentity }}<small>会员权益不区分私域、公域</small></span>
              <strong>{{ formatBenefits(ledger.totals.private.invitee) }}</strong>
            </div>
            <div class="scope-totals">
            <span>A · {{ inviterIdentity }}<small>叠加角色权益按订单范围计入</small></span>
              <p><em>若本单属于私域订单</em><strong>{{ formatBenefits(ledger.totals.private.inviter) }}</strong></p>
              <p><em>若本单属于公域订单</em><strong>{{ formatBenefits(ledger.totals.public.inviter) }}</strong></p>
            </div>
          </template>
          <template v-else>
            <div class="single-total">
              <span>B · {{ inviteeIdentity }}</span>
              <strong>{{ formatBenefits(ledger.totals.invitee) }}</strong>
            </div>
            <div class="single-total">
              <span>A · {{ inviterIdentity }}</span>
              <strong>{{ formatBenefits(ledger.totals.inviter) }}</strong>
            </div>
          </template>
        </section>
      </main>

      <aside class="side-column">
        <section class="side-card action-card">
          <el-button icon="el-icon-document-copy" @click="copyResult">复制计算结果</el-button>
          <el-button type="primary" icon="el-icon-refresh" @click="calculate">重新计算</el-button>
        </section>
      </aside>
    </div>
  </div>
</template>

<script>
import { calculateScenario } from "@/utils/pointsCalculator";
import { buildNarrativeLedger } from "@/utils/pointsCalculatorLedger";
import { formatBreakdownCopyLine } from "@/utils/pointsCalculatorCopy";

const initialForm = () => ({
  inviterLevel: "star",
  inviterRoles: ["partner"],
  inviteeLevel: "ordinary",
  inviteeRoles: [],
  contribution: 1000,
  directSharePct: 100,
  cityProvinceActive: true,
  citySharePct: 100,
  upgradedFromThisRelation: true
});

export default {
  name: "PointsCalculator",
  data() {
    return {
      form: initialForm(),
      errors: [],
      result: null,
      roleOptions: [
        { value: "partner", label: "合伙人" },
        { value: "city", label: "市级发起人" },
        { value: "province", label: "省级发起人" },
        { value: "region", label: "大区" }
      ],
      typeLabels: {
        tbc: "TBC",
        consumerRights: "消费者权益",
        couponRights: "消费券权益"
      }
    };
  },
  computed: {
    isStarToStar() {
      return this.form.inviterLevel === "star" && this.form.inviteeLevel === "star";
    },
    inviterIdentity() {
      return this.identityLabel(this.form.inviterLevel, this.form.inviterRoles);
    },
    inviteeIdentity() {
      return this.identityLabel(this.form.inviteeLevel, this.form.inviteeRoles);
    },
    hasRoleBenefits() {
      return this.form.inviterRoles.length > 0;
    },
    ledger() {
      if (!this.result) return { sharedSections: [], roleSections: [], totals: { mode: "single", inviter: {}, invitee: {} } };
      return buildNarrativeLedger(this.result.private, this.result.public);
    },
    sections() {
      return [...this.ledger.sharedSections, ...this.ledger.roleSections].map((section, index) => ({
        ...section,
        key: `${section.scope || "base"}-${section.title}-${index}`,
        scope: section.scope || "base"
      }));
    }
  },
  created() {
    this.calculate();
  },
  methods: {
    hasRole(role) {
      return this.form.inviterRoles.includes(role);
    },
    calculate() {
      try {
        const result = calculateScenario(this.form);
        if (result.missing.length) {
          this.errors = result.missing;
          return;
        }
        this.result = result;
        this.errors = [];
      } catch (error) {
        this.errors = [error.message];
      }
    },
    resetForm() {
      this.form = initialForm();
      this.calculate();
    },
    identityLabel(level, roles = []) {
      const levelText = level === "star" ? "星级会员" : "普通会员";
      const roleText = this.roleOptions.filter(role => roles.includes(role.value)).map(role => role.label).join("、");
      return roleText ? `${levelText}（${roleText}）` : levelText;
    },
    formatNumber(value) {
      return new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 4 }).format(value || 0);
    },
    formatBenefits(party) {
      const order = ["consumerRights", "tbc", "couponRights"];
      const values = order.filter(type => party[type] > 0).map(type => `${this.formatNumber(party[type])} ${this.typeLabels[type]}`);
      return values.length ? values.join(" + ") : "无权益";
    },
    buildCopyText() {
      const lines = [
        "信任桥积分发放试算结果",
        `${this.inviterIdentity} A 推荐 ${this.inviteeIdentity} B，贡献值：${this.form.contribution}`
      ];
      this.sections.forEach(section => {
        lines.push(`【${section.title}】`);
        section.items.forEach(item => lines.push(formatBreakdownCopyLine(item, this.formatNumber, this.typeLabels)));
      });
      lines.push("【计算结论】");
      if (this.ledger.totals.mode === "byScope") {
        lines.push(`B（会员权益不区分公私域）：${this.formatBenefits(this.ledger.totals.private.invitee)}`);
        lines.push(`A（私域订单）：${this.formatBenefits(this.ledger.totals.private.inviter)}`);
        lines.push(`A（公域订单）：${this.formatBenefits(this.ledger.totals.public.inviter)}`);
      } else {
        lines.push(`B：${this.formatBenefits(this.ledger.totals.invitee)}`);
        lines.push(`A：${this.formatBenefits(this.ledger.totals.inviter)}`);
      }
      lines.push("说明：会员升级看实际消费金额，权益计算看商品贡献值。");
      return lines.join("\n");
    },
    copyResult() {
      const text = this.buildCopyText();
      const fallback = () => {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(fallback);
      } else {
        fallback();
      }
      this.$message.success("计算结果已复制");
    }
  }
};
</script>

<style lang="scss" scoped>
.calculator-page {
  min-height: calc(100vh - 84px);
  padding: 28px;
  background: #f6f7fb;
  color: #202536;
}

.page-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;

  .eyebrow { margin: 0 0 5px; color: #7934f6; font-size: 12px; font-weight: 700; }
  h1 { margin: 0; font-size: 28px; line-height: 1.3; }
  .subtitle { margin: 7px 0 0; color: #7b8495; font-size: 13px; }
}

.scenario-card,
.result-card,
.side-card {
  background: #fff;
  border: 1px solid #e8eaf0;
  border-radius: 12px;
}

.scenario-card { padding: 18px 20px 0; }
.form-grid { display: grid; grid-template-columns: repeat(5, minmax(150px, 1fr)); gap: 14px; }
.form-grid ::v-deep .el-form-item { margin-bottom: 16px; }
.form-grid ::v-deep .el-form-item__label { padding: 0 0 6px; color: #697386; font-size: 12px; line-height: 20px; }
.form-grid ::v-deep .el-select,
.form-grid ::v-deep .el-input-number { width: 100%; }
.form-grid .el-form-item { position: relative; }
.input-suffix { position: absolute; right: 12px; bottom: 10px; color: #8790a0; }
.form-grid ::v-deep .el-input-number .el-input__inner { text-align: left; }

.parameter-row {
  min-height: 56px;
  margin: 0 -20px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 22px;
  border-top: 1px solid #eef0f4;
  background: #fafafe;
}
.parameter-label { color: #4f5a6d; font-size: 12px; font-weight: 700; }
.inline-number { display: flex; align-items: center; gap: 8px; color: #606b7c; font-size: 12px; }
.inline-number ::v-deep .el-input-number { width: 112px; }
.inline-number ::v-deep .el-input__inner { text-align: right; padding-right: 26px; }
.form-actions { display: flex; justify-content: flex-end; padding: 16px 0; }
.error-alert { margin-top: 14px; }

.result-layout { margin-top: 16px; display: grid; grid-template-columns: minmax(0, 1fr) 220px; gap: 16px; align-items: start; }
.result-card { padding: 18px; }
.section-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; margin-bottom: 14px; }
.section-heading h2,
.side-card h2 { margin: 0; font-size: 17px; }
.section-heading p { margin: 6px 0 0; color: #778296; font-size: 12px; line-height: 1.6; }

.ledger-group { margin-top: 12px; overflow: hidden; border: 1px solid #e5e8ee; border-radius: 9px; }
.ledger-group.scope-private { border-left: 3px solid #7934f6; }
.ledger-group.scope-public { border-left: 3px solid #36a275; }
.ledger-group-title { min-height: 48px; padding: 10px 13px; display: flex; align-items: center; justify-content: space-between; gap: 12px; background: #fafbfc; border-bottom: 1px solid #eceef2; }
.ledger-group-title h3 { margin: 0; color: #354052; font-size: 13px; }
.scope-chip { padding: 4px 8px; border-radius: 999px; font-size: 10px; font-weight: 700; }
.scope-chip.private { background: #f1eafd; color: #7934f6; }
.scope-chip.public { background: #eaf8f2; color: #2d8b64; }
.scope-chip.common { background: #eef1f6; color: #68758b; }
.formula-line { min-height: 54px; padding: 10px 13px; display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 18px; border-top: 1px solid #f0f1f4; }
.formula-line:first-of-type { border-top: 0; }
.formula-line > div { display: grid; gap: 4px; }
.formula-line strong { color: #505b6d; font-size: 12px; }
.formula-line small { color: #8790a0; font-size: 11px; }
.formula-line mark {
  width: fit-content;
  padding: 2px 7px;
  border-radius: 4px;
  background: #fff3df;
  color: #b76a17;
  font-size: 10px;
  line-height: 1.5;
}
.formula-line p { margin: 0; color: #697487; font-size: 11px; white-space: nowrap; }
.formula-line p span { margin-right: 4px; color: #9aa2af; }
.formula-line p b { color: #283448; font-size: 16px; }

.totals-card { margin-top: 14px; overflow: hidden; border: 1px solid #ded2f7; border-radius: 10px; background: #fbf9ff; }
.totals-title { padding: 12px 14px; border-bottom: 1px solid #e6def7; }
.totals-title span { color: #8978aa; font-size: 10px; }
.totals-title h3 { margin: 2px 0 0; font-size: 15px; }
.invitee-total,
.single-total { padding: 13px 14px; display: grid; grid-template-columns: minmax(190px, .8fr) 1.2fr; align-items: center; gap: 16px; }
.invitee-total { border-bottom: 1px solid #e6def7; }
.invitee-total span,
.single-total span,
.scope-totals > span { display: grid; gap: 3px; color: #59657a; font-size: 11px; font-weight: 700; }
.invitee-total small,
.scope-totals small { color: #929aaa; font-size: 9px; font-weight: 400; }
.invitee-total strong,
.single-total strong { color: #273349; font-size: 13px; }
.scope-totals { display: grid; grid-template-columns: minmax(190px, .8fr) 1fr 1fr; }
.scope-totals > span { padding: 13px 14px; align-content: center; }
.scope-totals p { margin: 0; padding: 13px 14px; display: grid; gap: 5px; border-left: 1px solid #e6def7; }
.scope-totals em { color: #7a8496; font-size: 10px; font-style: normal; }
.scope-totals strong { color: #273349; font-size: 13px; }

.side-column { display: grid; gap: 14px; }
.side-card h2 { padding: 16px; }
.action-card { padding: 14px; display: grid; grid-template-columns: 1fr; gap: 8px; }
.action-card .el-button { margin: 0; padding-inline: 8px; }

@media (max-width: 1280px) {
  .form-grid { grid-template-columns: repeat(3, minmax(160px, 1fr)); }
}

@media (max-width: 980px) {
  .calculator-page { padding: 18px; }
  .result-layout { grid-template-columns: 1fr; }
  .side-column { grid-template-columns: 1fr 1fr; }
  .action-card { align-self: start; }
}

@media (max-width: 680px) {
  .calculator-page {
    min-height: calc(100vh - 50px);
    padding: 12px 12px calc(20px + env(safe-area-inset-bottom));
    overflow-x: hidden;
  }
  .page-heading {
    margin-bottom: 12px;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  .page-heading h1 { font-size: 22px; }
  .page-heading .subtitle {
    display: block;
    margin-top: 5px;
    font-size: 12px;
    line-height: 1.55;
  }
  .page-heading > .el-button {
    width: 100%;
    min-height: 44px;
    margin: 0;
  }
  .scenario-card { padding: 14px 14px 0; }
  .form-grid { grid-template-columns: 1fr; }
  .form-grid ::v-deep .el-form-item { margin-bottom: 14px; }
  .form-grid ::v-deep .el-form-item__label {
    font-size: 13px;
    font-weight: 600;
  }
  .form-grid ::v-deep .el-input__inner,
  .form-grid ::v-deep .el-input-number,
  .form-grid ::v-deep .el-select {
    min-height: 44px;
    font-size: 16px;
  }
  .form-grid ::v-deep .el-input__inner { height: 44px; line-height: 44px; }
  .parameter-row {
    margin: 0 -14px;
    padding: 14px;
    align-items: stretch;
    flex-direction: column;
    gap: 14px;
  }
  .parameter-label { font-size: 13px; }
  .parameter-row ::v-deep .el-switch {
    min-height: 44px;
    display: flex;
    align-items: center;
  }
  .inline-number {
    min-height: 44px;
    justify-content: space-between;
    font-size: 13px;
  }
  .inline-number ::v-deep .el-input-number { width: 132px; }
  .inline-number ::v-deep .el-input__inner { height: 44px; font-size: 16px; }
  .form-actions { padding: 14px 0; }
  .form-actions .el-button {
    width: 100%;
    min-height: 46px;
    font-size: 15px;
  }
  .error-alert { margin-top: 12px; }
  .result-layout { margin-top: 12px; gap: 12px; }
  .result-card { padding: 12px; }
  .section-heading {
    margin-bottom: 10px;
    flex-direction: column;
    gap: 9px;
  }
  .section-heading h2,
  .side-card h2 { font-size: 16px; }
  .section-heading .el-tag { align-self: flex-start; }
  .ledger-group { margin-top: 10px; }
  .ledger-group-title {
    padding: 10px 11px;
    align-items: flex-start;
    flex-direction: column;
    gap: 7px;
  }
  .ledger-group-title h3 { line-height: 1.5; }
  .formula-line {
    min-height: 72px;
    padding: 11px;
    grid-template-columns: 1fr;
    align-items: start;
    gap: 7px;
  }
  .formula-line strong { font-size: 13px; line-height: 1.45; }
  .formula-line small { font-size: 12px; line-height: 1.55; word-break: break-word; }
  .formula-line mark { font-size: 11px; }
  .formula-line p { white-space: normal; }
  .formula-line p b { font-size: 17px; }
  .totals-title,
  .invitee-total,
  .single-total,
  .scope-totals > span,
  .scope-totals p { padding: 12px; }
  .invitee-total,
  .single-total,
  .scope-totals { grid-template-columns: 1fr; }
  .invitee-total,
  .single-total { gap: 8px; }
  .invitee-total strong,
  .single-total strong,
  .scope-totals strong { line-height: 1.55; word-break: break-word; }
  .scope-totals p { border-left: 0; border-top: 1px solid #e6def7; }
  .side-column { grid-template-columns: 1fr; }
  .action-card {
    padding: 12px;
    grid-template-columns: 1fr;
  }
  .action-card .el-button {
    min-height: 44px;
    font-size: 14px;
  }
}

@media (max-width: 360px) {
  .calculator-page { padding-right: 9px; padding-left: 9px; }
  .scenario-card { padding-right: 12px; padding-left: 12px; }
  .parameter-row { margin-right: -12px; margin-left: -12px; padding-right: 12px; padding-left: 12px; }
  .page-heading h1 { font-size: 20px; }
  .inline-number { align-items: flex-start; flex-direction: column; }
  .inline-number ::v-deep .el-input-number { width: 100%; }
}

@media (pointer: coarse) {
  .el-button { min-height: 44px; }
  .calculator-page ::v-deep .el-select-dropdown__item { min-height: 44px; line-height: 44px; }
}

@media (prefers-reduced-motion: reduce) {
  .calculator-page *,
  .calculator-page *::before,
  .calculator-page *::after {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
}
</style>
